import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Button, Card, ListGroup, Badge, Image } from "react-bootstrap";
import { GetAllProductAttributesWithValues } from "../Services/AttributeService";
import { GetAllProducts, AddProduct, UpdateProduct, GetProductById } from "../Services/ProductService";
import { GetAllCategories } from "../Services/CategoryService";
import { getImageUrl } from "../utils/imageUtils";
import bag_img from '../assets/images/bag_img.png';

export default function AddCustomProduct({ onClose, product }) {

    const isEdit = !!product;

    const loginUserId = localStorage.getItem("userId") ?? 6;

    const [categories, setCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]); 
    const [attributes, setAttributes] = useState([]);   

    const [currentCategoryId, setCurrentCategoryId] = useState("");
    const [currentCategoryName, setCurrentCategoryName] = useState("");
    const [filteredDropdownProducts, setFilteredDropdownProducts] = useState([]);

    const [mainProduct, setMainProduct] = useState(null); 
    
    const [subProducts, setSubProducts] = useState([]);

    const [selectedAttributes, setSelectedAttributes] = useState({}); 
    const [attributeDisplayForm, setAttributeDisplayForm] = useState({}); 

    const [tempXmlData, setTempXmlData] = useState(null);

    const [form, setForm] = useState({
        productName: "",
        price: "",
        description: "",
        vendorId: "",
        file: null,
        productImage: null
    });

    // Set vendorId from logged-in userId on mount and when product changes
    useEffect(() => {
        if (loginUserId) {
            setForm(prev => ({ ...prev, vendorId: loginUserId }));
        }
    }, [product]);

    useEffect(() => {
        loadCategories();
        loadAttributes();
        loadProducts();
    }, []);

    async function loadCategories() {
        try {
            const res = await GetAllCategories({ categoryNames: ["Wheat", "Grains", "Pulses"] });
            if (res.status && res.data) {
                setCategories(res.data);
                if (res.data.length > 0 && !currentCategoryId && !isEdit) {
                    handleCategoryChange(res.data[0]);
                }
            }
        } catch (err) { console.log(err); }
    }

    async function loadAttributes() {
        const res = await GetAllProductAttributesWithValues();
        if (res.status && res.data) setAttributes(res.data);
    }

    async function loadProducts() {
        const res = await GetAllProducts({ isCustomProduct: false });
        if (res.status && res.data) setAllProducts(res.data);
    }

    useEffect(() => {
        const fetchData = async () => {
          if (isEdit) {
            try {
              const res = await GetProductById(product.id);
              if (res.status && res.data) {
                const data = res.data;
    
                setForm({
                  productName: data.name || "",
                  price: data.price || "",
                  description: data.description || "",
                  file: null,
                  productImage: data.path
                });

                setCurrentCategoryId(1);
                setCurrentCategoryName("wheat");
                if (data.attributesXmlString) {
                    setTempXmlData(data.attributesXmlString);
                }
              }
            } catch (err) { console.error("Error fetching product details:", err); }
          }
        };
        fetchData();
      }, [product]);

    useEffect(() => {
        if (tempXmlData && allProducts.length > 0 && attributes.length > 0) {
            parseAndPopulateXmlData(tempXmlData);
        }
    }, [tempXmlData, allProducts, attributes]);

    const parseAndPopulateXmlData = (xmlString) => {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, "text/xml");
            
            const newSelAttrs = {};
            const newDispForm = {};

            const prodItem = xmlDoc.querySelector("Product > Item");
            let mainProductWeight = "";

            if (prodItem) {
                const id = prodItem.getAttribute("Id");
                const foundProd = allProducts.find(p => String(p.id) === String(id));
                
                mainProductWeight = prodItem.getAttribute("Weight"); 

                if (foundProd) {
                    setMainProduct(foundProd);
                    setCurrentCategoryName("wheat"); 
                }
            }

            const subItems = xmlDoc.querySelectorAll("SubProducts > Item");
            const loadedSubs = [];
            
            const pkgAttr = attributes.find(a => a.name === "Packaging Size");
            const pkgValues = pkgAttr ? pkgAttr.values : [];

            subItems.forEach(item => {
                const id = item.getAttribute("Id");
                const weightName = item.getAttribute("Weight"); 
                const foundSub = allProducts.find(p => String(p.id) === String(id));
                if (foundSub) {
                    const sizeOpt = pkgValues.find(v => v.name === weightName);
                    loadedSubs.push({
                        ...foundSub,
                        selectedSize: weightName,
                        selectedSizeId: sizeOpt ? sizeOpt.id : "" 
                    });
                }
            });
            setSubProducts(loadedSubs);

            const attrItems = xmlDoc.querySelectorAll("Attributes > Item");
            attrItems.forEach(item => {
                const attrId = item.getAttribute("Id");
                const valId = item.getAttribute("ValueId");
                const valName = item.getAttribute("Value");
                const attrName = item.getAttribute("Name");

                if (!newSelAttrs[attrId]) newSelAttrs[attrId] = [];
                newSelAttrs[attrId].push(valId);

                const fieldName = toFieldName(attrName);
                newDispForm[fieldName] = valName;
                newDispForm[`${fieldName}_Label`] = attrName;
            });

            if (pkgAttr && mainProductWeight) {
                const valObj = pkgValues.find(v => v.name === mainProductWeight);
                
                if (valObj) {
                    if (!newSelAttrs[pkgAttr.id]) newSelAttrs[pkgAttr.id] = [];
                    
                    if(!newSelAttrs[pkgAttr.id].includes(valObj.id)) {
                        newSelAttrs[pkgAttr.id].push(valObj.id);
                    }

                    const fieldName = toFieldName(pkgAttr.name);
                    newDispForm[fieldName] = valObj.name;
                    newDispForm[`${fieldName}_Label`] = pkgAttr.name;
                }
            }

            setSelectedAttributes(newSelAttrs);
            setAttributeDisplayForm(newDispForm);

        } catch (e) {
            console.error("Error parsing XML", e);
        }
    };

    const handleCategoryChange = (category) => {
        setCurrentCategoryId(category.id);
        setCurrentCategoryName(category.name.toLowerCase());
    };

    useEffect(() => {
        if (currentCategoryId && allProducts.length > 0) {
            const filtered = allProducts.filter(p => String(p.categoryId) === String(currentCategoryId));
            setFilteredDropdownProducts(filtered);
        }
    }, [currentCategoryId, allProducts]);

    const getPackagingOptions = () => {
        const attr = attributes.find(a => a.name === "Packaging Size");
        return attr ? attr.values : [];
    };

    const handleProductSelectInDropdown = (selectedProdId) => {
        if(!selectedProdId) return;
        const productObj = allProducts.find(p => String(p.id) === String(selectedProdId));
        if (!productObj) return;

        if (currentCategoryName === "wheat") {
            setMainProduct(productObj);
        } else {
            setSubProducts(prev => {
                const exists = prev.find(p => p.id === productObj.id);
                if (exists) return prev.filter(p => p.id !== productObj.id);
                
                const packOptions = getPackagingOptions();
                const defaultPack = packOptions.length > 0 ? packOptions[0] : { name: "1 Kg", id: "0" };
                
                return [...prev, { 
                    ...productObj, 
                    selectedSize: defaultPack.name, 
                    selectedSizeId: defaultPack.id
                }]; 
            });
        }
    };

    const handleSubProductSizeChange = (prodId, newSizeValueId) => {
        const packOptions = getPackagingOptions();
        const selectedOption = packOptions.find(p => String(p.id) === String(newSizeValueId));

        setSubProducts(prev => prev.map(p => {
            if (p.id === prodId) {
                return { 
                    ...p, 
                    selectedSize: selectedOption?.name || "1 Kg", 
                    selectedSizeId: newSizeValueId 
                };
            }
            return p;
        }));
    };

    const handleAttributeSelect = (attributeName, selectedValueName, isMultiple) => {
        const attribute = attributes.find(a => a.name.toLowerCase() === attributeName.toLowerCase());
        if (!attribute) return;
        const valueObj = attribute.values.find(v => v.name === selectedValueName);
        if (!valueObj) return;

        setSelectedAttributes(prev => {
            const currentIds = prev[attribute.id] || [];
            let newIds = isMultiple
                ? (currentIds.includes(valueObj.id) ? currentIds.filter(id => id !== valueObj.id) : [...currentIds, valueObj.id])
                : [valueObj.id];
            return { ...prev, [attribute.id]: newIds };
        });

        const fieldName = toFieldName(attributeName);
        setAttributeDisplayForm(prev => ({ ...prev, [fieldName]: valueObj.name, [`${fieldName}_Label`]: attributeName }));
    };

    const getWeightMultiplier = (weightStr) => {
        if (!weightStr) return 1;
        const match = weightStr.toString().match(/(\d+(\.\d+)?)/);
        return match ? parseFloat(match[0]) : 1;
    };

    const generateXmlPayload = () => {
        let xml = "<Catalog>";
        if (mainProduct) {
            const selectedMainSize = attributeDisplayForm["packagingSize"] || "1 Kg"; 
            const qty = getWeightMultiplier(selectedMainSize);
            const unitPrice = parseFloat(mainProduct.price);
            const totalPrice = (unitPrice * qty).toFixed(2);

            xml += "<Product>";
            xml += `<Item Id='${mainProduct.id}' Name='${mainProduct.name}' Price='${unitPrice}' TotalPrice='${totalPrice}' Weight='${selectedMainSize}' />`;
            xml += "</Product>";
        }

        if (subProducts.length > 0) {
            xml += "<SubProducts>";
            subProducts.forEach(sub => {
                const subSize = sub.selectedSize || "1 Kg";
                const qty = getWeightMultiplier(subSize); 
                const unitPrice = parseFloat(sub.price);
                const totalPrice = (unitPrice * qty).toFixed(2); 

                xml += `<Item Id='${sub.id}' Name='${sub.name}' Price='${unitPrice}' TotalPrice='${totalPrice}' Weight='${subSize}' />`;
            });
            xml += "</SubProducts>";
        }

        const attrKeys = Object.keys(selectedAttributes);
        if (attrKeys.length > 0) {
            xml += "<Attributes>";
            attrKeys.forEach(attrId => {
                const attrDef = attributes.find(a => a.id == attrId);
                if (attrDef && attrDef.name.toLowerCase() === "packaging size") {
                    return;
                }

                const selectedValIds = selectedAttributes[attrId];
                if (attrDef && selectedValIds.length > 0) {
                    selectedValIds.forEach(valId => {
                        const valDef = attrDef.values.find(v => v.id == valId);
                        if (valDef) {
                            xml += `<Item Id="${attrId}" Name="${attrDef.name}" ValueId="${valId}" Value="${valDef.name}" IncreasePrice="${valDef.increasePrice || 0}" />`;
                        }
                    });
                }    
            });
            xml += "</Attributes>";
        }
        xml += "</Catalog>";
        return xml;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!mainProduct) { alert("Please select a Wheat product"); return; }
        const xmlString = generateXmlPayload();
        
        const formData = new FormData();
        formData.append("name", form.productName);
        formData.append("description", form.description);
        formData.append("price", form.price);
        formData.append("categoryId", 6);
        formData.append("vendorId", loginUserId); // Include vendorId
        formData.append("isCustomProduct", "true");
        formData.append("attributesXml", xmlString);
        if (form.file) formData.append("file", form.file);

        let res;
        if (isEdit) res = await UpdateProduct(product.id, formData);
        else res = await AddProduct(formData);

        if (res && res.status) onClose();
        else alert(res?.message || "Error saving product");
    };

    const toFieldName = (label) => label.replace(/[^a-zA-Z0-9 ]/g, "").split(" ").map((p, i) => i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1)).join("");
    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "file" ? files[0] : value }));
    };
    const getAttrOptions = (name) => (attributes.find(a => a.name === name)?.values || []).map(v => v.name);
    const packagingOptionsList = getPackagingOptions();

    const CustomDropdown = ({ label, options = [], onChange, value }) => {
        const [open, setOpen] = useState(false);
        const [search, setSearch] = useState("");
        const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
        return (
            <Form.Group className="mb-3 position-relative">
                <Form.Label>{label}</Form.Label>
                <div className="form-control d-flex justify-content-between align-items-center" onClick={() => setOpen(!open)} style={{cursor:'pointer'}}>
                    <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{value || "Select..."}</span>
                    <span>▼</span>
                </div>
                {open && (
                    <div className="position-absolute w-100 bg-white border rounded shadow p-2" style={{zIndex: 1000, maxHeight: 200, overflowY:'auto'}}>
                         <input autoFocus type="text" className="form-control mb-2" placeholder="Search..." onChange={e => setSearch(e.target.value)} />
                         {filtered.map(opt => (
                            <div key={opt} className="p-1 border-bottom" style={{cursor:'pointer'}} onClick={() => { onChange(opt); setOpen(false); }}>{opt}</div>
                         ))}
                    </div>
                )}
            </Form.Group>
        );
    };

    return (
        <>
            <div className="overlay" style={{position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', zIndex:999}} onClick={onClose}></div>
            <div className="bg-white p-4 rounded shadow" style={{position:'fixed', top:'5%', left:'10%', width:'80%', height:'90%', overflowY:'auto', zIndex:1000}}>
                <div className="d-flex justify-content-between mb-4"><h4 className="fw-bold">{isEdit ? "Edit" : "Create"} Custom Product</h4><button onClick={onClose} className="btn btn-close"></button></div>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={7} className="border-end pe-4">
                            {/* Image Upload */}
                            <div className="d-flex align-items-center mb-4 gap-3">
                                <div className="upload-circle">
                                {form.file || form.productImage ? (
                                    <Image src={form.file ? URL.createObjectURL(form.file) : getImageUrl(form.productImage, bag_img)} roundedCircle className="preview-img" />
                                ) : ( <span>Upload</span> )}
                                </div>
                                <Form.Control type="file" name="file" accept="image/*" onChange={handleChange} style={{ display: "none" }} id="image-upload-input" />
                                <label htmlFor="image-upload-input" style={{ cursor: "pointer" }}>Select Image</label>
                            </div>
                            
                            {/* Basic Info */}
                            <Row>
                                <Col md={6}><Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control name="productName" value={form.productName} onChange={handleChange} /></Form.Group></Col>
                                <Col md={6}><Form.Group className="mb-3"><Form.Label>Price</Form.Label><Form.Control name="price" value={form.price} onChange={handleChange} /></Form.Group></Col>
                            </Row>
                            <Row><Col md={12}><Form.Group className="mb-3"><Form.Label>Description</Form.Label><Form.Control name="description" as="textarea" rows={3} value={form.description} onChange={handleChange} /></Form.Group></Col></Row>

                            {/* Category Selection */}
                            <div className="mb-3">
                                <Form.Label className="fw-bold">1. Select Category</Form.Label>
                                <div className="d-flex gap-3 mt-1">
                                    {categories.map(cat => (
                                        <div key={cat.id} onClick={() => handleCategoryChange(cat)}
                                            className={`px-3 py-2 border rounded cursor-pointer ${String(currentCategoryId) === String(cat.id) ? 'bg-warning text-white fw-bold' : 'bg-light'}`}>
                                            {cat.name}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Product Dropdown */}
                            <div className="mb-4">
                                <Form.Label className="fw-bold">2. Select {currentCategoryName} Product</Form.Label>
                                <Form.Select value={currentCategoryName === 'wheat' ? (mainProduct?.id || "") : ""} onChange={(e) => handleProductSelectInDropdown(e.target.value)}>
                                    <option value="">-- Choose Item --</option>
                                    {filteredDropdownProducts.map(p => {
                                        const isSelected = currentCategoryName === 'wheat' ? mainProduct?.id === p.id : subProducts.some(sp => sp.id === p.id);
                                        return <option key={p.id} value={p.id} className={isSelected ? 'fw-bold text-success' : ''}>{p.name} {isSelected ? "✓" : ""}</option>
                                    })}
                                </Form.Select>
                            </div>

                            {/* Attributes (Wheat) */}
                            {currentCategoryName === 'wheat' && mainProduct && (
                                <div className="p-3 bg-light rounded border">
                                    <h6 className="fw-bold mb-3">Configure {mainProduct.name}</h6>
                                    <Row>
                                        <Col md={6}><CustomDropdown label="Grinding Texture" value={attributeDisplayForm["grindingTexture"]} options={getAttrOptions("Grinding Texture")} onChange={(val) => handleAttributeSelect("Grinding Texture", val, false)} /></Col>
                                        <Col md={6}><CustomDropdown label="Packaging" value={attributeDisplayForm["packaging"]} options={getAttrOptions("Packaging")} onChange={(val) => handleAttributeSelect("Packaging", val, false)} /></Col>
                                        <Col md={6}><CustomDropdown label="Packaging Size" value={attributeDisplayForm["packagingSize"]} options={getAttrOptions("Packaging Size")} onChange={(val) => handleAttributeSelect("Packaging Size", val, false)} /></Col>
                                    </Row>
                                </div>
                            )}
                        </Col>

                        <Col md={5}>
                            <h5 className="mb-3">Selected Configuration</h5>
                            <Card className="shadow-sm" style={{ border: '2px solid #dc3545', minHeight: '300px' }}>
                                <Card.Header className="bg-danger text-white">Your Custom Pack</Card.Header>
                                <Card.Body style={{maxHeight: '400px', overflowY: 'auto'}}>
                                    {mainProduct ? (
                                        <div className="mb-3 pb-3 border-bottom">
                                            <div className="d-flex justify-content-between"><h6 className="fw-bold text-primary">{mainProduct.name}</h6><Badge bg="success">₹{mainProduct.price}</Badge></div>
                                            <div className="mt-2 ps-2 border-start border-3">
                                                <small className="text-muted d-block fw-bold">Configuration:</small>
                                                {Object.keys(attributeDisplayForm).filter(k => !k.endsWith('_Label')).map(key => {
                                                    const label = attributeDisplayForm[`${key}_Label`] || key;
                                                    return <div key={key} className="small text-dark">• {label}: <b>{attributeDisplayForm[key]}</b></div>
                                                })}
                                            </div>
                                        </div>
                                    ) : ( <div className="alert alert-warning small">Please select a Wheat product first.</div> )}

                                    {subProducts.length > 0 && (
                                        <div>
                                            <h6 className="fw-bold text-secondary mt-3">Add-ons (Grains/Pulses)</h6>
                                            <ListGroup variant="flush">
                                                {subProducts.map((sub, idx) => (
                                                    <ListGroup.Item key={idx} className="ps-0 pe-0">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span className="fw-bold">{sub.name}</span>
                                                            <Button size="sm" variant="outline-danger" style={{padding: '0px 6px', lineHeight:1}} onClick={() => setSubProducts(subProducts.filter(s => s.id !== sub.id))}>×</Button>
                                                        </div>
                                                        <div className="d-flex gap-2 align-items-center">
                                                            <div className="flex-grow-1"><Badge bg="info">₹{sub.price}</Badge></div>
                                                            <div style={{width: '130px'}}>
                                                                <Form.Select size="sm" value={sub.selectedSizeId} onChange={(e) => handleSubProductSizeChange(sub.id, e.target.value)}>
                                                                    {packagingOptionsList.map(opt => ( <option key={opt.id} value={opt.id}>{opt.name}</option> ))}
                                                                </Form.Select>
                                                            </div>
                                                        </div>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                            <div className="mt-4 d-grid"><Button size="lg" variant="success" type="submit">{isEdit ? "UPDATE" : "CREATE"} PRODUCT</Button></div>
                        </Col>
                    </Row>
                </Form>
            </div>
        </>
    );
}